/**
 * PPM Settings Tab Component
 * Handles PPM/PPK calculation settings, grade thresholds, and score mappings
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { RotateCcw, AlertTriangle, Info, Target } from 'lucide-react';
import { SupplierPerformanceSettings } from '../../../lib/api';
import { PerformanceFormData } from './types';
import { useQualityMeasureLabel, useQualityMeasureLongLabel } from '../../../lib/qualityLabels';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { PPMGradeThresholdsSection } from './PPMGradeThresholdsSection';
import { usePPMGradeThresholds } from './usePPMGradeThresholds';
import { ScoreGradeBadge } from './ScoreGradeBadge';
import { KpiTargetRulesTable } from './KpiTargetRulesTable';

interface PPMSettingsTabProps {
  settings: SupplierPerformanceSettings | null;
  formData: PerformanceFormData;
  /** CR-6652: PPM colour band state, owned by usePPMGradeThresholds */
  ppmGrades: ReturnType<typeof usePPMGradeThresholds>;
  onInputChange: (field: string, value: string | number | boolean) => void;
  onResetPPMMappings: () => void;
}

export function PPMSettingsTab({
  settings,
  formData,
  ppmGrades,
  onInputChange,
  onResetPPMMappings,
}: PPMSettingsTabProps) {
  const measure = useQualityMeasureLabel();
  const measureLong = useQualityMeasureLongLabel();
  // Prefer the raw boolean from MenuSettingsContext over comparing against
  // the 'PPK' string — keeps the toggle semantics single-source-of-truth.
  const { qualityMeasurePpk } = useMenuSettings();
  const divisorLabel = qualityMeasurePpk ? '1,000' : '1,000,000';
  const unitNoun = qualityMeasurePpk ? 'thousand' : 'million';
  return (
    <div className="space-y-4">
      {/* KPI Target Value */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            <CardTitle>{measure} Target Value</CardTitle>
          </div>
          <CardDescription>
            Set the target value for {measure}. This appears as a goal line on dashboard graphs.
            Add target ranges to change the target over time. A range saves as soon as you save its row; the default
            target saves with Save Changes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-xs space-y-2">
            <Label htmlFor="ppm_target" className="flex items-center gap-2">
              <Target className="h-4 w-4 text-orange-600" />
              Default {measure} Target
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="ppm_target"
                type="number"
                min="0"
                value={formData.ppm_target}
                onChange={(e) => onInputChange('ppm_target', parseInt(e.target.value) || 0)}
              />
              <span>{measure}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Target value shown as goal line on {measure} charts (lower is better), for any date no {measure} target range covers
            </p>
          </div>
          {/* CR-6266: ranges that replace the default target between dates. */}
          <div className="mt-6">
            <KpiTargetRulesTable kpi="ppm" label={measure} unit={measure} />
          </div>
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <Info className="h-4 w-4" />
              <span className="font-medium">Target vs. Grade Thresholds</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Target value sets the goal line on charts. Configure the {measure} Grade Thresholds below to define the colour coding bands for quality performance values.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            <CardTitle>{measure} ({measureLong}) Settings</CardTitle>
          </div>
          <CardDescription>
            Configure how {measure} quality metrics are calculated and displayed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Formula Info */}
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              <span className="font-medium">{measure} Calculation Formula</span>
            </div>
            <code className="block text-sm p-2 bg-background rounded border">
              {measure} = (Reject Quantity / Total Received Quantity) x {divisorLabel}
            </code>
            <p className="text-sm text-muted-foreground">
              {measure} measures the defect rate per {unitNoun} parts. Lower values indicate better quality performance.
            </p>
          </div>

          {/* CR-6652: Configurable PPM Grade Thresholds */}
          <PPMGradeThresholdsSection
            bands={settings?.ppm_grade_thresholds || []}
            editingBand={ppmGrades.editingBand}
            bandEditData={ppmGrades.bandEditData}
            savingBand={ppmGrades.savingBand}
            showAddBand={ppmGrades.showAddBand}
            newBandData={ppmGrades.newBandData}
            addingBand={ppmGrades.addingBand}
            resettingBands={ppmGrades.resettingBands}
            qualityLabel={measure}
            onSetShowAddBand={ppmGrades.setShowAddBand}
            onResetBands={ppmGrades.handleResetBands}
            onEditBand={ppmGrades.handleEditBand}
            onCancelEditBand={ppmGrades.handleCancelEditBand}
            onSaveBand={ppmGrades.handleSaveBand}
            onBandFieldChange={ppmGrades.handleBandFieldChange}
            onAddBand={ppmGrades.handleAddBand}
            onDeleteBand={ppmGrades.handleDeleteBand}
            onNewBandFieldChange={ppmGrades.handleNewBandFieldChange}
          />
        </CardContent>
      </Card>

      {/* Score Mappings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{measure} Score Mappings</CardTitle>
              <CardDescription>
                Map {measure} values to score points for rating calculations.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={onResetPPMMappings}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {settings?.ppm_score_mappings && settings.ppm_score_mappings.length > 0 ? (
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-4 text-sm font-medium text-muted-foreground pb-2 border-b">
                <div>Min {measure}</div>
                <div>Max {measure}</div>
                <div>Score Points</div>
                <div>Grade</div>
              </div>
              {settings.ppm_score_mappings.map((mapping) => (
                <div key={mapping.id} className="grid grid-cols-4 gap-4 items-center py-2">
                  <div>{mapping.min_ppm.toLocaleString()}</div>
                  <div>{mapping.max_ppm.toLocaleString()}</div>
                  <div className="font-medium">{mapping.mapped_score} pts</div>
                  <div>
                    {/* CR-6269: the grade these points earn on the Supplier
                        Grade Thresholds tab. */}
                    <ScoreGradeBadge score={mapping.mapped_score} bands={settings.rating_thresholds} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">
              No {measure} score mappings configured. Click "Reset to Defaults" to create default mappings.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
